import type { ReactNode } from 'react'
import { FilterX } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import styles from './FilterBar.module.css'

interface FilterBarProps {
  /** The search input — given its own row so it never shrinks to nothing. */
  search?: ReactNode
  children?: ReactNode
  actions?: ReactNode
  /** Shows the reset control and the count of what is currently narrowing the list. */
  activeFilterCount?: number
  onReset?: () => void
  className?: string
}

export function FilterBar({ search, children, actions, activeFilterCount = 0, onReset, className }: FilterBarProps) {
  return (
    <div className={cn(styles.bar, className)} data-print="hide">
      <div className={styles.primary}>
        {search ? <div className={styles.search}>{search}</div> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>

      {children ? (
        <div className={styles.filters}>
          {children}
          {activeFilterCount > 0 && onReset ? (
            <Button variant="ghost" size="sm" onClick={onReset} iconLeft={<FilterX aria-hidden="true" />}>
              Clear {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

interface FilterSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  /** Label for the "no constraint" option. */
  allLabel?: string
}

/** Compact labelled select used for status and type filters. */
export function FilterSelect({ label, value, onChange, options, allLabel = 'All' }: FilterSelectProps) {
  return (
    <label className={styles.filter}>
      <span className={styles.filterLabel}>{label}</span>
      <span className={cn(styles.filterControl, value !== '' && styles.filterActive)}>
        <select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
          <option value="">{allLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    </label>
  )
}

interface FilterDateProps {
  label: string
  value: string
  onChange: (value: string) => void
  max?: string
  min?: string
}

export function FilterDate({ label, value, onChange, max, min }: FilterDateProps) {
  return (
    <label className={styles.filter}>
      <span className={styles.filterLabel}>{label}</span>
      <span className={cn(styles.filterControl, value !== '' && styles.filterActive)}>
        <input
          type="date"
          value={value}
          max={max}
          min={min}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label}
        />
      </span>
    </label>
  )
}
